import { getAugmentDefaultSvgPath, AUGMENT_KIND } from './augmentAssets';

const EVENT_ICONS = {
  penalty: getAugmentDefaultSvgPath('penalty', AUGMENT_KIND.MATCH),
  missedPenalty: getAugmentDefaultSvgPath('missed-pk', AUGMENT_KIND.MATCH),
  redCard: getAugmentDefaultSvgPath('red-card', AUGMENT_KIND.MATCH)
};

/** @typedef {{ kind: string, minute?: number, extraMinute?: number, side?: string, player?: string, detail?: string }} MatchEvent */

/** @param {Record<string, unknown> | null | undefined} game */
export function getRedCardCounts(game) {
  if (!game) return { home: 0, away: 0 };
  return {
    home: Math.max(0, Number(game.redCardsHome ?? game.rch) || 0),
    away: Math.max(0, Number(game.redCardsAway ?? game.rca) || 0)
  };
}

/** @param {Record<string, unknown> | null | undefined} game */
export function shouldShowMatchEvents(game) {
  const status = game?.status;
  return status === 'live' || status === 'finished';
}

/** @param {Record<string, unknown> | null | undefined} game */
export function hasVisibleMatchEvents(game) {
  if (!shouldShowMatchEvents(game)) return false;
  if (hasMatchEventTimeline(game)) return true;
  const { home, away } = getRedCardCounts(game);
  return home > 0 || away > 0;
}

/** @param {MatchEvent | null | undefined} event */
export function formatEventMinute(event) {
  if (!event) return '';
  const base = Number(event.minute) || 0;
  const extra = Number(event.extraMinute) || 0;
  if (extra > 0) return `${base}+${extra}'`;
  return `${base}'`;
}

/** @param {MatchEvent} event */
function isRedCardEvent(event) {
  return event.kind === 'card' && (event.detail === 'rc' || event.detail === 'yrc');
}

/**
 * Events we surface in the match card UI (goals, scored/missed pens, red cards).
 * @param {MatchEvent} event
 */
export function isDisplayableMatchEvent(event) {
  if (!event) return false;
  if (event.kind === 'goal' || event.kind === 'missed_penalty') return true;
  if (isRedCardEvent(event)) return true;
  return false;
}

/**
 * @param {Record<string, unknown> | null | undefined} game
 * @returns {MatchEvent[]}
 */
export function getMatchEventTimeline(game) {
  const raw = game?.matchEvents;
  if (!Array.isArray(raw)) return [];
  return raw.filter(isDisplayableMatchEvent);
}

/**
 * @param {Record<string, unknown> | null | undefined} game
 * @param {'home' | 'away'} side
 * @returns {MatchEvent[]}
 */
export function getMatchEventsForSide(game, side) {
  return getMatchEventTimeline(game).filter((ev) => ev.side === side);
}

/** @param {Record<string, unknown> | null | undefined} game */
export function hasMatchEventTimeline(game) {
  return getMatchEventTimeline(game).length > 0;
}

/**
 * @param {MatchEvent} event
 * @returns {{ icon?: string, suffix?: string, label: string }}
 */
export function getMatchEventDisplay(event) {
  const minute = formatEventMinute(event);
  const player = String(event.player || '').trim();
  const playerPart = player || 'Unknown';

  if (event.kind === 'goal') {
    if (event.detail === 'p') {
      return {
        icon: EVENT_ICONS.penalty,
        label: `${minute} ${playerPart} (pen)`
      };
    }
    if (event.detail === 'og') {
      return {
        label: `${minute} ${playerPart} (OG)`
      };
    }
    return {
      label: `${minute} ${playerPart}`
    };
  }

  if (event.kind === 'missed_penalty') {
    return {
      icon: EVENT_ICONS.missedPenalty,
      label: `${minute} ${playerPart} (missed pen)`
    };
  }

  if (isRedCardEvent(event)) {
    const cardLabel = event.detail === 'yrc' ? 'second yellow' : 'red card';
    return {
      icon: EVENT_ICONS.redCard,
      label: `${minute} ${playerPart} (${cardLabel})`
    };
  }

  return { label: `${minute} ${playerPart}` };
}
